import Vue from 'vue';
import makeStoreDebugAccessible from '../util/storeDebug';
import { Font } from '../types/pageContract';
import ClientSettingsApiService from '../components/ui/user-settings/readability-settings/ClientSettingsApiService';
import { Site } from '../types/initialData';
import {
  ClientSettings,
  Readabilities,
  Settings
} from '../types/userSettingServiceTypes';
import ReadAloudStore from '../components/ui/read-aloud/library/read-aloud.store';
import SearchStore from './search.store';
import CookiesStore from '@/components/ui/cookies/cookies.store';

interface IReadabilityState {
  textSizeScale: number;
  currentFont: Font | null | {};
  externalFonts: Array<Font>;
  focusMode: boolean;
  darkMode: boolean;
  letterSpacingScale: number;
  lineHeightScale: number;
  hideFocusModePrompt: boolean;
  leftDrawerWidth: number;
}

export interface IStoredSettingsForReadability {
  textSize: number;
  fontFamily: Font | null;
  letterSpacing: number;
  lineHeight: number;
}

const DEFAULT_SETTINGS_FOR_READABILITY: IStoredSettingsForReadability = {
  fontFamily: null,
  textSize: 100,
  lineHeight: 100,
  letterSpacing: 0
};

export const state: IReadabilityState = Vue.observable({
  textSizeScale: DEFAULT_SETTINGS_FOR_READABILITY.textSize,
  focusMode: false,
  letterSpacingScale: DEFAULT_SETTINGS_FOR_READABILITY.letterSpacing,
  lineHeightScale: DEFAULT_SETTINGS_FOR_READABILITY.lineHeight,
  hideFocusModePrompt: false,
  darkMode: false,
  leftDrawerWidth: 320,
  currentFont: DEFAULT_SETTINGS_FOR_READABILITY.fontFamily,
  externalFonts: [
    {
      name: 'Libre Baskerville',
      defaultSize: '17px'
    },
    {
      name: 'Montserrat',
      defaultSize: '17px'
    },
    {
      name: 'Newsreader',
      defaultSize: '17px'
    },
    {
      name: 'Open Dyslexic',
      url: 'local',
      defaultSize: '17px'
    },
    {
      name: 'Open Sans',
      defaultSize: '17px'
    }
  ]
});

class ClientSettingsStore {
  constructor() {
    makeStoreDebugAccessible('readabilityStore', state);
  }

  async fetchClientSettings(site: Site): Promise<void> {
    let fetchedSettings: ClientSettings =
      await ClientSettingsApiService.getSettings(site);
    let fetchedReadabilities: Readabilities =
      fetchedSettings.settings?.readabilities;

    // If client settings don't exist - post default settings to the service
    if (!fetchedSettings || !fetchedReadabilities) {
      // Exit if cookies are not allowed
      if (!CookiesStore.functionalCookiesAllowed) {
        return;
      }
      let internalReadabilities: Readabilities;

      let payload: Settings;
      if (
        localStorage.getItem('readability-settings') ||
        localStorage.getItem('local-config.leftDrawerWidth') ||
        localStorage.getItem('read-aloud-store') ||
        localStorage.getItem('search.recent-searches')
      ) {
        // The api cannot take null as font family
        internalReadabilities = {
          ...JSON.parse(localStorage.getItem('readability-settings') as string)
        };
        if (internalReadabilities.fontFamily == null) {
          internalReadabilities.fontFamily = {};
        }

        payload = {
          readabilities: internalReadabilities,
          darkMode: state.darkMode,
          featureGalleryVersion: 0,
          leftDrawerWidth:
            JSON.parse(
              localStorage.getItem('local-config.leftDrawerWidth') as string
            ) || state.leftDrawerWidth,
          readAloudStore: JSON.parse(
            localStorage.getItem('read-aloud-store') as string
          ),
          recentSearches:
            JSON.parse(
              localStorage.getItem('search.recent-searches') as string
            ) || []
        };

        localStorage.removeItem('readability-settings');
        localStorage.removeItem('local-config.leftDrawerWidth');
        localStorage.removeItem('read-aloud-store');
        localStorage.removeItem('search.recent-searches');
      } else {
        payload = {
          readabilities: {
            ...DEFAULT_SETTINGS_FOR_READABILITY,
            fontFamily: {}
          },
          darkMode: state.darkMode,
          featureGalleryVersion: 0,
          leftDrawerWidth: state.leftDrawerWidth,
          readAloudStore: {
            currentVoiceSelection: ReadAloudStore.voice,
            currentSpeedSelection: ReadAloudStore.speed
          },
          recentSearches: SearchStore.recentlySearchedList
        };
      }

      fetchedSettings = await ClientSettingsApiService.updateSettings(
        payload,
        site
      );
      fetchedReadabilities = fetchedSettings.settings.readabilities;
    }

    const { textSize, lineHeight, letterSpacing } = fetchedReadabilities;
    let fontFamily = fetchedReadabilities.fontFamily;

    if (JSON.stringify(fetchedReadabilities.fontFamily) === '{}') {
      fontFamily = null;
    }

    state.textSizeScale = textSize;
    state.letterSpacingScale = letterSpacing;
    state.lineHeightScale = lineHeight;
    state.currentFont = fontFamily;
    state.darkMode = fetchedSettings.settings.darkMode;
    state.leftDrawerWidth = fetchedSettings.settings.leftDrawerWidth;
    // Set read aloud store
    if (fetchedSettings.settings.readAloudStore.currentSpeedSelection) {
      ReadAloudStore.speed =
        fetchedSettings.settings.readAloudStore.currentSpeedSelection;
    }
    if (fetchedSettings.settings.readAloudStore.currentVoiceSelection) {
      ReadAloudStore.voice =
        fetchedSettings.settings.readAloudStore.currentVoiceSelection;
    }

    SearchStore.recentlySearchedList = fetchedSettings.settings.recentSearches;

    // Clearing leftover local storage
    if (localStorage.getItem('readability-settings')) {
      localStorage.removeItem('readability-settings');
    }

    if (localStorage.getItem('local-config.leftDrawerWidth')) {
      localStorage.removeItem('local-config.leftDrawerWidth');
    }

    if (localStorage.getItem('read-aloud-store')) {
      localStorage.removeItem('read-aloud-store');
    }

    if (localStorage.getItem('search.recent-searches')) {
      localStorage.removeItem('search.recent-searches');
    }
  }

  public async updateClientSettings(site: Site): Promise<void> {
    // Exit if cookies are not allowed
    if (!CookiesStore.functionalCookiesAllowed) {
      return;
    }
    const payload = {
      readabilities: {
        letterSpacing: state.letterSpacingScale,
        lineHeight: state.lineHeightScale,
        textSize: state.textSizeScale,
        fontFamily:
          state.currentFont !== null ? state.currentFont : ({} as Font)
      },
      darkMode: state.darkMode,
      featureGalleryVersion: 0,
      leftDrawerWidth: state.leftDrawerWidth,
      readAloudStore: {
        currentVoiceSelection: ReadAloudStore.voice,
        currentSpeedSelection: ReadAloudStore.speed
      },
      recentSearches: SearchStore.recentlySearchedList
    };
    await ClientSettingsApiService.updateSettings(payload, site);
  }

  public get externalFonts(): Array<Font> {
    return state.externalFonts;
  }

  public get currentTextSizeScale(): number {
    return state.textSizeScale;
  }

  public get currentLetterSpacingScale(): number {
    return state.letterSpacingScale;
  }

  public get currentLineHeightScale(): number {
    return state.lineHeightScale;
  }

  public get currentFont(): Font | null | {} {
    return state.currentFont;
  }

  public get focusMode(): boolean {
    return state.focusMode;
  }

  public set focusMode(val: boolean) {
    state.focusMode = val;
  }

  public get darkMode(): boolean {
    return state.darkMode;
  }

  public get hideFocusModePrompt(): boolean {
    return state.hideFocusModePrompt;
  }

  public get leftDrawerWidth(): number {
    return state.leftDrawerWidth;
  }

  public get defaultSettingsChanged(): boolean {
    return (
      DEFAULT_SETTINGS_FOR_READABILITY.fontFamily !== state.currentFont ||
      DEFAULT_SETTINGS_FOR_READABILITY.letterSpacing !==
        state.letterSpacingScale ||
      DEFAULT_SETTINGS_FOR_READABILITY.lineHeight !== state.lineHeightScale ||
      DEFAULT_SETTINGS_FOR_READABILITY.textSize !== state.textSizeScale
    );
  }

  public get textSizeChanged(): boolean {
    return state.textSizeScale > 100;
  }

  public get letterSpacingChanged(): boolean {
    return state.letterSpacingScale > 0;
  }

  public changeFontFamily(fontName: string | null, site: Site): void {
    // We check that the font is part of the list, to only update or set localstorage if it is
    const fontInList: Font | undefined = state.externalFonts.find(
      (font: Font) => font.name === fontName
    );
    if (fontInList) {
      state.currentFont = fontInList;
    } else {
      state.currentFont = null;
    }
    this.updateClientSettings(site);
  }

  public changeTextSizeScale(newTextSizeScale: number, site: Site): void {
    state.textSizeScale = newTextSizeScale;
    if (this.textSizeChanged) {
      document.documentElement.style.setProperty(
        '--text-scale',
        `${newTextSizeScale / 100}`
      );
    } else {
      document.documentElement.style.setProperty('--text-scale', '1');
    }
    this.updateClientSettings(site);
  }

  public changeLineHeight(newLineHeightScale: number, site: Site): void {
    state.lineHeightScale = newLineHeightScale;
    this.updateClientSettings(site);
  }

  public changeLetterSpacing(newLetterSpacingScale: number, site: Site): void {
    state.letterSpacingScale = newLetterSpacingScale;
    this.updateClientSettings(site);
  }

  public changeFocusMode(focusModeVal: boolean): void {
    state.focusMode = focusModeVal;
  }

  public changeDarkMode(darkModeVal: boolean, site: Site): void {
    state.darkMode = darkModeVal;
    this.updateClientSettings(site);
  }

  public rememberToHideFocusModePrompt(): void {
    state.hideFocusModePrompt = true;
  }

  public setLeftDrawerWidth(
    widthPixels: number,
    site: Site,
    shouldUpdate: boolean
  ) {
    state.leftDrawerWidth = widthPixels;

    if (shouldUpdate) {
      this.updateClientSettings(site);
    }
  }

  public resetReadabilitySettingsToDefault(site: Site): void {
    state.currentFont = null;
    state.textSizeScale = DEFAULT_SETTINGS_FOR_READABILITY.textSize;
    state.lineHeightScale = DEFAULT_SETTINGS_FOR_READABILITY.lineHeight;
    state.letterSpacingScale = DEFAULT_SETTINGS_FOR_READABILITY.letterSpacing;
    this.updateClientSettings(site);
  }
}

export default new ClientSettingsStore();
